import { Component, Input, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { PortalAdminService } from 'src/app/services/portal-admin.service';

interface CandidateNote {
  id: number;
  action: string;
  commentingPersonName: number;
  comments: string;
  dateAdded: string;
  dateLastModified: string;
  isDeleted: boolean;
  jobOrder: any;
  personReference: number;
  personReferenceType: string;
}

@Component({
  selector: 'app-candidate-notes',
  templateUrl: './candidate-notes.component.html',
  styleUrls: ['./candidate-notes.component.css']
})
export class CandidateNotesComponent implements OnInit {
  @Input() candidateID: string = '';
  @Input() candidateName: string = '';
  notes: CandidateNote[] = [];
  noteTypes: string[] = [];
  loading: boolean = true;
  error: boolean = false;
  errorMessage: string = '';  
  isNoteModalVisible: boolean = false;
  modalLoading: boolean = false;
  modalSuccessMessage: string = '';
  model: any;

  constructor(private http: HttpClient, private portalAdminService: PortalAdminService) {}

  ngOnInit(): void {
    this.loadNotes();
    this.loadNoteTypes()
  }

  loadNotes(): void {
    this.loading = true;
    this.error = false;
    const url = `${environment.apiConfig.uri}/admin/Candidates/GetCandidateNotes/${this.candidateID}`;
    
    this.http.get<any>(url).subscribe(
      (response) => {
        if (response.isSuccess) {
          const rawNotes = response.data || [];          
          // Filter notes based on criteria:
          const filteredNotes = rawNotes.filter((note: any) => {
            const matchesType = note.personReferenceType?.toString().toLowerCase() === 'candidate';
            const matchesReference = note.personReference?.toString() === this.candidateID.toString();
            // console.log(`Note ${note.id}: personReferenceType=${note.personReferenceType}, personReference=${note.personReference}, candidateID=${this.candidateID}, matches=${matchesType && matchesReference}`);
            return matchesType && matchesReference;
          });
          
          // console.log("Filtered notes:", filteredNotes);
          
          // Map the filtered API response to our expected format
          this.notes = filteredNotes.map((note: any) => ({
            id: note.id,
            action: note.action,
            comments: note.comments,
            commentingPersonName: note.commentingPersonName,
            dateAdded: this.formatDate(note.dateAdded),
            dateLastModified: note.dateLastModified,
            isDeleted: note.isDeleted,
            jobOrder: note.jobOrder,
            personReference: note.personReference,
            personReferenceType: note.personReferenceType
          }));
          
          // console.log("Final mapped notes:", this.notes);
          
          // Sort by newest first (using dateAdded as primary, dateCreated as fallback)
          this.notes.sort((a, b) => {
            const dateA = new Date(a.dateAdded).getTime();
            const dateB = new Date(b.dateAdded).getTime();
            return dateB - dateA;
          });
          
        } else {
          this.handleError('Failed to load notes');
        }
        this.loading = false;
      },
      (error) => {
        // console.error('Error loading notes:', error);
        this.handleError('Failed to load notes. Please try again.');
        this.loading = false;
      }
    );
  }

  loadNoteTypes(): void {
    const url = `${environment.apiConfig.uri}/admin/Candidates/GetNoteActions`;
    this.http.get<any>(url).subscribe(
      (response) => {
        if (response.isSuccess) {
          // console.log('Note types loaded successfully:', response.data);
          // Store the note types from API response
          this.noteTypes = response.data || [];
        } else {
          this.handleError('Failed to load note types');
        }
      },
      (error) => {
        this.handleError('Failed to load note types. Please try again.');
      }
    );
  }

  private formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch (error) {
      return dateString;
    }
  }

  private handleError(message: string): void {
    this.error = true;
    this.errorMessage = message;
    this.notes = [];
  }

  retryLoad(): void {
    this.loadNotes();
  }

  // Modal methods
  openAddNoteModal(): void {
    this.isNoteModalVisible = true;
    this.modalSuccessMessage = '';
  }

  onNoteModalClose(): void {
    this.isNoteModalVisible = false;
    this.modalSuccessMessage = '';
    this.modalLoading = false;
  }

  saveNote(noteData: {type: string, comments: string}): void {
    this.modalLoading = true;
    const url = `${environment.apiConfig.uri}/admin/Candidates/AddNoteToBullhorn`;
    
    // Get email from token using the service
    const email = this.portalAdminService.getEmailFromToken() || localStorage.getItem('email') || '';
    
    const payload = {
      action: noteData.type,
      comments: noteData.comments,
      personReference: parseInt(this.candidateID),
      email: email
    };
    
    this.http.post<any>(url, payload).subscribe(
      (response) => {
        if (response.isSuccess) {
          this.modalSuccessMessage = 'Note saved successfully.';
          setTimeout(() => {
            this.onNoteModalClose();
            this.loadNotes(); // Refresh the notes list
          }, 1500);
        } else {
          this.modalSuccessMessage = 'Failed to save note.';
          setTimeout(() => {
            this.onNoteModalClose();
          }, 2000);
        }
        this.modalLoading = false;
      },
      (error) => {
        // console.error('Error saving note:', error);
        this.modalSuccessMessage = 'Error saving note.';
        this.modalLoading = false;
        setTimeout(() => {
          this.onNoteModalClose();
        }, 2000);
      }
    );
  }

  // TrackBy function for better performance with *ngFor
  trackByNoteId(index: number, note: CandidateNote): number {
    return note.id;
  }
}